import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell, UserPlus, ChatCircleText, ChatTeardropDots, Stack, Check } from "@phosphor-icons/react";
import { timeAgo, formatDateTime } from "@/components/ProfileWidgets";

const TYPE_ICONS = { follow: UserPlus, comment_reply: ChatCircleText, message: ChatTeardropDots, new_volume: Stack };

export default function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    async function poll() {
      try {
        const { data } = await api.get("/me/notifications/unread-count");
        if (alive) setUnread(data.count || 0);
      } catch { /* noop */ }
    }
    poll();
    const t = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  useEffect(() => {
    function onClickOutside(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  async function toggleOpen() {
    const next = !open;
    setOpen(next);
    if (next) {
      try {
        const { data } = await api.get("/me/notifications");
        setItems(data);
        if (unread > 0) {
          await api.post("/me/notifications/read-all");
          setUnread(0);
        }
      } catch { /* noop */ }
    }
  }

  function goTo(n) {
    setOpen(false);
    if (n.link) navigate(n.link);
  }

  if (!user) return null;

  return (
    <div className="relative" ref={boxRef} data-testid="notifications-bell">
      <button
        onClick={toggleOpen}
        className="relative w-10 h-10 rounded-lg hover:bg-white/5 grid place-items-center text-zinc-300 hover:text-white transition-colors"
        data-testid="notifications-bell-btn"
        aria-label="Notificações"
      >
        <Bell size={20} weight={unread > 0 ? "fill" : "bold"} />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#E63946] text-white text-[10px] font-bold grid place-items-center leading-none" data-testid="notifications-unread-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 w-80 max-w-[92vw] bg-[#121217] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50" data-testid="notifications-dropdown">
          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
            <span className="font-display font-bold text-sm">Notificações</span>
            <Bell size={16} weight="fill" className="text-[#E63946]" />
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-white/5">
            {items.length === 0 && (
              <div className="p-8 text-center text-zinc-500 font-mono-accent text-xs" data-testid="notifications-empty">SEM NOTIFICAÇÕES</div>
            )}
            {items.map((n) => {
              const Icon = TYPE_ICONS[n.type] || Bell;
              return (
                <button
                  key={n.notification_id}
                  onClick={() => goTo(n)}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5 ${n.read ? "" : "bg-[#E63946]/5"}`}
                  data-testid={`notification-item-${n.notification_id}`}
                >
                  <div className="relative flex-shrink-0">
                    {n.actor_picture ? (
                      <img src={resolveImageUrl(n.actor_picture)} alt="" className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-xs font-display font-bold">
                        {(n.actor_name || "?")[0]?.toUpperCase()}
                      </div>
                    )}
                    <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#121217] grid place-items-center text-[#E63946]">
                      <Icon size={12} weight="fill" />
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-200 leading-snug">{n.message}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">{timeAgo(n.created_at)} · {formatDateTime(n.created_at)}</div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-[#E63946] flex-shrink-0 mt-1.5" />}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => { setOpen(false); navigate("/notificacoes"); }}
            className="w-full py-3 text-center text-sm text-[#E63946] hover:bg-white/5 font-display font-semibold border-t border-white/5"
            data-testid="notifications-see-all"
          >
            Ver todas as notificações
          </button>
        </div>
      )}
    </div>
  );
}
